/**
 * AddProductDialog — three-tab modal that adds line items to a manual
 * customer order. Replaces (and absorbs) the inline "+ Marketplace Picker"
 * pattern in AdminOrdersBoard, so admins have one consistent entry point
 * for every way to populate a quote.
 *
 *   ┌─ 📦 Marketplace Picker    ── existing 3700+ SKU catalog + custom
 *   ├─ ✍️ Manual Entry          ── one product, file upload OR paste URL,
 *   │                              optional "Also save to marketplace"
 *   └─ 📋 CSV Import            ── paste or upload .csv, per-row preview
 *                                  with validation errors, then import
 *
 * The dialog returns (via onAdd) an array of line items in the format the
 * order's items[] array expects: {name, sku, quantity, uom, unit_cost,
 * image_url, description}.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Link as LinkIcon,
  PackagePlus,
  FileSpreadsheet,
  X,
  Check,
  AlertCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helper: build an absolute URL for any path the backend returns ─────
const absolutize = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/static/")) return `${API}${url}`;
  if (url.startsWith("/static/")) return `${API}/api${url}`;
  return url;
};

export default function AddProductDialog({
  open,
  onClose,
  onAdd,
  token,
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/api/admin/orders/marketplace-categories`, { headers })
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl w-[92vw] max-h-[88vh] overflow-hidden flex flex-col p-0"
        data-testid="add-product-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <PackagePlus className="w-5 h-5 text-indigo-600" />
            Add Products — Manual or CSV
          </DialogTitle>
          <DialogDescription>
            Build one product with image, or paste/upload a CSV of many. Tick &ldquo;Also save to marketplace&rdquo; to make them reusable on future quotes.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="manual" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-3 self-start" data-testid="apd-tabs">
            <TabsTrigger value="manual" data-testid="apd-tab-manual">
              <PackagePlus className="w-3.5 h-3.5 mr-1.5" /> Manual Entry
            </TabsTrigger>
            <TabsTrigger value="csv" data-testid="apd-tab-csv">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> CSV Import
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="flex-1 overflow-auto px-6 pt-3 pb-5 data-[state=inactive]:hidden">
            <ManualEntryTab
              token={token}
              categories={categories}
              onAdd={(item) => { onAdd([item]); onClose(); }}
            />
          </TabsContent>

          <TabsContent value="csv" className="flex-1 overflow-auto px-6 pt-3 pb-5 data-[state=inactive]:hidden">
            <CsvImportTab
              token={token}
              categories={categories}
              onAdd={(items) => { onAdd(items); onClose(); }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


// ════════════════════════════ Manual Entry Tab ═════════════════════════════
function ManualEntryTab({ token, categories, onAdd }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [draft, setDraft] = useState({
    name: "",
    sku: "",
    quantity: 1,
    uom: "Each",
    unit_cost: "",
    internal_cost: "",    // What WE pay the supplier. Never shown to customer.
    category: "",
    image_url: "",
    description: "",
    brand: "",
    supplier: "",
    tagline: "",
    features: "",
    specs: "",
    brochure_url: "",
    badge: "",
  });
  const [imageMode, setImageMode] = useState("upload"); // 'upload' | 'url'
  const [uploading, setUploading] = useState(false);
  const [saveToMarketplace, setSaveToMarketplace] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileRef = useRef(null);

  // Derived markup % shown next to internal cost so admin sees their margin live.
  const markupPct = (() => {
    const ic = parseFloat(draft.internal_cost);
    const uc = parseFloat(draft.unit_cost);
    if (!ic || !uc || ic <= 0) return null;
    return ((uc - ic) / ic) * 100;
  })();

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // NOTE: Let axios auto-generate the multipart Content-Type header (with
      // its boundary=…) — manually setting "multipart/form-data" strips the
      // boundary and causes FastAPI to reject the payload as unparseable.
      const r = await axios.post(`${API}/api/admin/orders/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDraft((d) => ({ ...d, image_url: absolutize(r.data.url) }));
      toast.success(`Uploaded ${(r.data.size_bytes / 1024).toFixed(1)} KB`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const errs = [];
    if (!draft.name.trim()) errs.push("Name is required");
    const qty = parseInt(draft.quantity);
    if (!qty || qty < 1) errs.push("Quantity must be a positive integer");
    if (saveToMarketplace && !draft.category) errs.push("Pick a category before saving to marketplace");
    if (errs.length) { toast.error(errs.join(" · ")); return; }

    // 1) Save to marketplace catalog if checkbox ticked
    if (saveToMarketplace) {
      try {
        await axios.post(`${API}/api/admin/orders/custom-products`, {
          name: draft.name.trim(),
          sku: draft.sku?.trim() || null,
          category: draft.category,
          brand: draft.brand?.trim() || null,
          tagline: draft.tagline?.trim() || null,
          description: draft.description?.trim() || null,
          image_url: draft.image_url || null,
          customer_price: draft.unit_cost ? parseFloat(draft.unit_cost) : null,
          features: draft.features ? draft.features.split(/[|;\n]+/).map((s) => s.trim()).filter(Boolean) : null,
          specs: draft.specs ? Object.fromEntries(
            draft.specs.split(/[|;\n]+/)
              .map((kv) => kv.split(":").map((s) => s.trim()))
              .filter((kv) => kv.length === 2 && kv[0])
          ) : null,
          brochure_url: draft.brochure_url?.trim() || null,
          badge: draft.badge?.trim() || null,
        }, { headers });
        toast.success("Saved to marketplace catalog");
      } catch (e) {
        toast.error(`Couldn't save to catalog: ${e.response?.data?.detail || e.message}`);
        return;
      }
    }

    // 2) Hand the line item back to the parent (category + internal_cost
    //    travel with it so they're persisted on the order).
    onAdd({
      name: draft.name.trim(),
      sku: draft.sku?.trim() || null,
      quantity: qty,
      uom: draft.uom || "Each",
      unit_cost: draft.unit_cost === "" ? null : parseFloat(draft.unit_cost),
      internal_cost: draft.internal_cost === "" ? null : parseFloat(draft.internal_cost),
      category: draft.category || null,
      supplier: draft.supplier?.trim() || null,
      image_url: draft.image_url || null,
      description: draft.description?.trim() || null,
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Left col — required fields */}
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Product Name *</label>
          <Input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Sterile Gauze Pad 4x4" data-testid="apd-manual-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">SKU / Model #</label>
            <Input value={draft.sku || ""} onChange={(e) => setDraft({ ...draft, sku: e.target.value })} placeholder="WD-44" data-testid="apd-manual-sku" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Supplier <span className="text-slate-400 normal-case tracking-normal font-normal">(for spend rollup)</span></label>
            <Input value={draft.supplier || ""} onChange={(e) => setDraft({ ...draft, supplier: e.target.value })} placeholder="Vive Health" data-testid="apd-manual-supplier" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Brand <span className="text-slate-400 normal-case tracking-normal font-normal">(if different from supplier)</span></label>
          <Input value={draft.brand || ""} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} placeholder="3M / Vive Precision" data-testid="apd-manual-brand" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Qty *</label>
            <Input type="number" min="1" value={draft.quantity ?? ""} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} data-testid="apd-manual-qty" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">UOM</label>
            <Input value={draft.uom || ""} onChange={(e) => setDraft({ ...draft, uom: e.target.value })} placeholder="Each" data-testid="apd-manual-uom" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Category</label>
            <Select value={draft.category || ""} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger className="h-9" data-testid="apd-manual-category">
                <SelectValue placeholder="Pick…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`apd-manual-category-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Unit $ <span className="text-slate-400 normal-case tracking-normal font-normal">(customer pays)</span></label>
            <Input type="number" step="0.01" min="0" value={draft.unit_cost ?? ""} onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value })} data-testid="apd-manual-cost" placeholder="0.00" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              <span className="text-amber-700">🔒 Internal Cost</span> <span className="text-slate-400 normal-case tracking-normal font-normal">(your cost · admin-only)</span>
            </label>
            <Input type="number" step="0.01" min="0" value={draft.internal_cost ?? ""} onChange={(e) => setDraft({ ...draft, internal_cost: e.target.value })} data-testid="apd-manual-internal-cost" placeholder="0.00" className="border-amber-300 focus:border-amber-500" />
          </div>
        </div>
        {markupPct != null && (
          <div className="text-[11px] -mt-1 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded text-emerald-800 font-semibold" data-testid="apd-manual-markup">
            Markup: <span className="font-mono">{markupPct.toFixed(1)}%</span>
            <span className="ml-2 text-emerald-600 font-normal">— never shown to customer</span>
          </div>
        )}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Description</label>
          <Textarea rows={3} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="One-line summary that appears on the quote PDF." data-testid="apd-manual-desc" />
        </div>
      </div>

      {/* Right col — image + advanced */}
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Product Image</label>
          <div className="flex gap-1 mb-2">
            <button type="button" onClick={() => setImageMode("upload")} className={`text-[11px] px-2.5 py-1 rounded border font-semibold ${imageMode === "upload" ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-slate-200 text-slate-500"}`} data-testid="apd-manual-img-tab-upload">
              <Upload className="w-3 h-3 inline mr-1" /> Upload File
            </button>
            <button type="button" onClick={() => setImageMode("url")} className={`text-[11px] px-2.5 py-1 rounded border font-semibold ${imageMode === "url" ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-slate-200 text-slate-500"}`} data-testid="apd-manual-img-tab-url">
              <LinkIcon className="w-3 h-3 inline mr-1" /> Paste URL
            </button>
          </div>
          {imageMode === "upload" ? (
            <div className="border-2 border-dashed border-slate-300 rounded p-4 text-center text-xs text-slate-500 hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors cursor-pointer" onClick={() => fileRef.current?.click()} data-testid="apd-manual-upload-dropzone">
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="apd-manual-upload-input" />
              {uploading ? "Uploading…" : draft.image_url ? "Click to replace image" : "Click or drag a JPG/PNG (≤8 MB)"}
            </div>
          ) : (
            <Input value={draft.image_url || ""} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} placeholder="https://supplier.com/path/to/image.jpg" data-testid="apd-manual-url-input" />
          )}
          {draft.image_url && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
              <img src={draft.image_url} alt="" className="w-10 h-10 rounded object-cover bg-white" data-testid="apd-manual-preview-img" />
              <div className="text-[11px] text-slate-600 flex-1 truncate font-mono">{draft.image_url}</div>
              <button type="button" onClick={() => setDraft({ ...draft, image_url: "" })} className="text-slate-400 hover:text-red-600" data-testid="apd-manual-clear-img">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        <details className="border border-slate-200 rounded" open={showAdvanced} onToggle={(e) => setShowAdvanced(e.target.open)}>
          <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2 hover:bg-slate-50" data-testid="apd-manual-advanced-toggle">
            Advanced — marketplace fields
          </summary>
          <div className="p-3 space-y-2 border-t border-slate-200">
            <div>
              <label className="text-[10px] font-bold text-slate-500 block mb-1">Tagline (1-line marketing pitch)</label>
              <Input value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} placeholder="Clinical-grade · 1 case = 100 ct" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 block mb-1">Features (one per line, or | separated)</label>
              <Textarea rows={2} value={draft.features} onChange={(e) => setDraft({ ...draft, features: e.target.value })} placeholder="Sterile pre-saturated&#10;Lint-free weave&#10;FDA-cleared" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 block mb-1">Specs (key: value, one per line)</label>
              <Textarea rows={2} value={draft.specs} onChange={(e) => setDraft({ ...draft, specs: e.target.value })} placeholder="Dimensions: 4&quot; × 4&quot;&#10;Ply: 12&#10;UPC: 8001234567890" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-slate-500 block mb-1">Spec Sheet URL</label>
                <Input value={draft.brochure_url} onChange={(e) => setDraft({ ...draft, brochure_url: e.target.value })} placeholder="https://…/spec.pdf" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 block mb-1">Badge</label>
                <Input value={draft.badge} onChange={(e) => setDraft({ ...draft, badge: e.target.value })} placeholder="New / Best Seller" />
              </div>
            </div>
          </div>
        </details>

        <div className="p-3 bg-slate-50 border border-slate-200 rounded">
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox checked={saveToMarketplace} onCheckedChange={setSaveToMarketplace} data-testid="apd-manual-save-checkbox" />
            <div>
              <div className="text-sm font-semibold text-slate-800">Also save to marketplace catalog</div>
              <div className="text-[11px] text-slate-500">Reuse this product on future quotes — uses the Category above.</div>
            </div>
          </label>
        </div>

        <Button onClick={submit} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="apd-manual-submit">
          <Check className="w-4 h-4 mr-1.5" /> Add to Order
        </Button>
      </div>
    </div>
  );
}


// ═════════════════════════════ CSV Import Tab ══════════════════════════════
function CsvImportTab({ token, categories, onAdd }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saveToMarketplace, setSaveToMarketplace] = useState(false);
  const [category, setCategory] = useState("");
  const fileRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = (e) => setCsvText(e.target.result || "");
    r.readAsText(file);
  };

  const parse = async () => {
    if (!csvText.trim()) { toast.error("Paste or upload CSV first"); return; }
    setParsing(true);
    try {
      const r = await axios.post(`${API}/api/admin/orders/parse-csv`, { csv_text: csvText }, { headers });
      setPreview(r.data);
      if (r.data.error_count) toast.warning(`${r.data.valid_count} valid · ${r.data.error_count} need fixing`);
      else toast.success(`${r.data.valid_count} rows ready to import`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV parse failed");
      setPreview(null);
    } finally {
      setParsing(false);
    }
  };

  const importNow = async () => {
    if (!preview) return;
    const valid = preview.rows.filter((r) => r._valid);
    if (valid.length === 0) { toast.error("No valid rows to import"); return; }
    if (saveToMarketplace && !category) { toast.error("Pick a marketplace category first"); return; }

    // Persist marketplace records if requested
    if (saveToMarketplace) {
      try {
        for (const row of valid) {
          await axios.post(`${API}/api/admin/orders/custom-products`, {
            name: row.name,
            sku: row.sku || null,
            category: row.category || category,
            brand: row.brand || null,
            tagline: row.tagline || null,
            description: row.description || null,
            image_url: row.image_url || null,
            customer_price: row.unit_cost ?? null,
            features: row.features || null,
            specs: row.specs || null,
            applications: row.applications || null,
            brochure_url: row.brochure_url || null,
            badge: row.badge || null,
          }, { headers });
        }
        toast.success(`${valid.length} products saved to catalog`);
      } catch (e) {
        toast.error(`Some catalog saves failed: ${e.response?.data?.detail || e.message}`);
      }
    }

    // Hand line items back to parent
    onAdd(valid.map((r) => ({
      name: r.name,
      sku: r.sku || null,
      quantity: r.quantity,
      uom: r.uom || "Each",
      unit_cost: r.unit_cost ?? null,
      internal_cost: r.internal_cost ?? null,
      category: r.category || category || null,
      supplier: r.supplier || null,
      image_url: r.image_url || null,
      description: r.description || null,
    })));
  };

  return (
    <div className="space-y-3">
      {/* Step 1 — Source */}
      <div className="border border-slate-200 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Step 1 · Paste or upload CSV</span>
          <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 flex items-center gap-1" data-testid="apd-csv-file-btn">
            <Upload className="w-3.5 h-3.5" /> Upload .csv
            <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="apd-csv-file-input" />
          </button>
        </div>
        <Textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          rows={6}
          className="font-mono text-xs"
          placeholder="name,sku,quantity,uom,unit_cost,internal_cost,category,image_url,description
Scalpel #10,SC-10,100,Each,3.50,1.75,surgical,,Single-use disposable scalpels
Nitrile Gloves L,NG-L,1000,Box,15.50,8.20,ppe,https://...,Powder-free nitrile gloves"
          data-testid="apd-csv-textarea"
        />
        <div className="mt-2 flex gap-2 items-center">
          <Button size="sm" onClick={parse} disabled={parsing || !csvText.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="apd-csv-parse-btn">
            {parsing ? "Parsing…" : "Preview"}
          </Button>
          <p className="text-[11px] text-slate-500">Required: <code className="font-mono bg-slate-100 px-1 rounded">name</code>, <code className="font-mono bg-slate-100 px-1 rounded">quantity</code>. Optional: sku, uom, unit_cost (customer), <span className="text-amber-700 font-semibold">internal_cost (your cost — admin-only)</span>, category, image_url, description, brand, features, specs, brochure_url, badge.</p>
        </div>
      </div>

      {/* Step 2 — Preview */}
      {preview && (
        <div className="border border-slate-200 rounded">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50 border-b border-slate-200">
            <div className="text-xs">
              <span className="font-bold text-slate-800">Step 2 · Preview</span>
              <span className="ml-3 text-emerald-700">✓ {preview.valid_count} valid</span>
              {preview.error_count > 0 && <span className="ml-3 text-red-700">✗ {preview.error_count} need fixing</span>}
              <span className="ml-3 text-slate-500">schema: <span className="font-mono">{preview.schema_detected}</span> · {preview.delimiter}-delimited</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <Checkbox checked={saveToMarketplace} onCheckedChange={setSaveToMarketplace} data-testid="apd-csv-save-checkbox" />
                <span className="text-slate-700">Also save to catalog</span>
              </label>
              {saveToMarketplace && (
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-8 w-44" data-testid="apd-csv-category"><SelectValue placeholder="Category…" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button size="sm" onClick={importNow} disabled={preview.valid_count === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="apd-csv-import-btn">
                <Check className="w-4 h-4 mr-1" /> Import {preview.valid_count} rows
              </Button>
            </div>
          </div>
          {preview.unknown_headers?.length > 0 && (
            <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800">
              <AlertCircle className="w-3 h-3 inline mr-1" /> Unrecognized columns ignored: <code className="font-mono">{preview.unknown_headers.join(", ")}</code>
            </div>
          )}
          <div className="overflow-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left">Row</th>
                  <th className="px-2 py-1.5 text-left">Name</th>
                  <th className="px-2 py-1.5 text-left">SKU</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Unit $</th>
                  <th className="px-2 py-1.5 text-right text-amber-700" title="Your cost — admin-only">🔒 Internal</th>
                  <th className="px-2 py-1.5 text-left">Category</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r._row} className={r._valid ? "border-b border-slate-100" : "border-b border-red-100 bg-red-50/40"} data-testid={`apd-csv-row-${r._row}`}>
                    <td className="px-2 py-1.5 font-mono text-slate-500">{r._row}</td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate">{r.name || <em className="text-red-600">missing</em>}</td>
                    <td className="px-2 py-1.5 font-mono text-slate-500">{r.sku || "—"}</td>
                    <td className="px-2 py-1.5 text-right">{r.quantity || <em className="text-red-600">—</em>}</td>
                    <td className="px-2 py-1.5 text-right">{r.unit_cost != null ? `$${Number(r.unit_cost).toFixed(2)}` : "—"}</td>
                    <td className="px-2 py-1.5 text-right text-amber-700 font-semibold">{r.internal_cost != null ? `$${Number(r.internal_cost).toFixed(2)}` : "—"}</td>
                    <td className="px-2 py-1.5 text-slate-500 text-[11px]">{r.category || "—"}</td>
                    <td className="px-2 py-1.5">
                      {r._valid ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Ready</span>
                      ) : (
                        <span className="text-red-700">{r._errors.join("; ")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
